import css from "@css/modules/Layout.module.scss"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ArrowDown } from "@css/icons/SVG"
import Image from "next/image"
import cn from "classnames"
import { BlocksRenderer } from "@strapi/blocks-react-renderer"
import { BlockLink, MegaMenu } from "@t/GeneralData"
import { usePathname } from "next/navigation"

function isAncestor(element: HTMLElement, potentialAncestor: HTMLElement) {
    if (!element || !potentialAncestor) {
        return false
    }

    let currentElement = element.parentElement

    while (currentElement) {
        if (currentElement === potentialAncestor) {
            return true
        }
        currentElement = currentElement.parentElement
    }

    return false
}

export type MegaMenuProps = {
    simpleMegaMenu?: boolean
    title: string
    mega: MegaMenu
}

export function SellMegaMenu({ title, simpleMegaMenu, mega }: MegaMenuProps) {
    const [element, setElement] = useState(null)
    const liRef = useRef<HTMLLIElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    useEffect(() => {
        const target = document.getElementById("dialog-portal")
        if (target) {
            const element = document.createElement("div")
            setElement(element)
            target.appendChild(element)
            return () => {
                target.removeChild(element)
            }
        }
    }, [])
    useEffect(() => {
        let isVisible = false
        const li = liRef.current
        const mobileMedia = window.matchMedia("(min-width: 991px)")
        let closeTimeout
        function close(force = false) {
            closeTimeout && clearTimeout(closeTimeout)
            const doClose = () => {
                isVisible = false
                setOpen(false)
            }
            if (force) {
                doClose() // force closed
            } else {
                closeTimeout = setTimeout(doClose, 100)
            }
        }

        const closeMenu = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (isVisible) {
                const ancestor = isAncestor(target, liRef.current) || isAncestor(target, menuRef.current)
                if (mobileMedia.matches && isVisible && target !== liRef.current && target !== menuRef.current && !ancestor) {
                    close()
                } else if (ancestor) {
                    closeTimeout && clearTimeout(closeTimeout)
                }
            }
        }
        const onMouseOver = () => {
            if (menuRef.current && mobileMedia.matches) {
                closeTimeout && clearTimeout(closeTimeout)
                isVisible = true
                Object.assign(menuRef.current.style, { top: `${window.scrollY + li.offsetTop + li.offsetHeight}px` })
                setOpen(true)
            }
        }
        const onScroll = () => {
            if (menuRef.current && isVisible) {
                close(true)
            }
        }
        const onClick = () => {
            setOpen((o) => !o)
        }
        if (li) {
            li.addEventListener("mouseover", onMouseOver)
            li.addEventListener("click", onClick)
            window.addEventListener("scroll", onScroll)
            document.body.addEventListener("mousemove", closeMenu)
            return () => {
                li.removeEventListener("mouseover", onMouseOver)
                li.removeEventListener("click", onClick)
                window.removeEventListener("scroll", onScroll)
                document.body.removeEventListener("mousemove", closeMenu)
            }
        }
    }, [])
    const iterate = (i: BlockLink) => {
        return (
            <section className={css.sellMegaMenuSection} key={i.id}>
                {i?.icon && <Image className={css.sellMegaMenuSectionImg} src={i.icon.url} alt={i.icon.alternativeText || "Section img"} width={80} height={80} />}
                <div className={css.sellMegaMenuSectionRight}>
                    <div className={css.menuGroupTitle}>{i.title}</div>
                    <div className={css.megaMenuSectionDesc}>{i.description}</div>
                    <a className={css.sellMegaMenuReadMore} href={i.link}>
                        Read more
                    </a>
                </div>
            </section>
        )
    }
    let menuContent
    if (element && !simpleMegaMenu) {
        menuContent = createPortal(
            <div className={cn(css.megaMenu, { [css.megaMenuOpen]: open })} ref={menuRef}>
                <div className={css.sellMegaMenu}>
                    <div className={css.sellMegaMenuGroup1}>
                        <div className={css.menuGroupLinks}>
                            <div className={css.menuGroupTitle}>{mega.linksTitle}</div>
                            <BlocksRenderer content={mega.links} />
                        </div>
                    </div>
                    <div className={css.sellMegaMenuGroup2}>{mega.blockLinks?.filter((i) => i.column !== 2)?.map(iterate)}</div>

                    <div className={css.sellMegaMenuGroup3}>{mega.blockLinks?.filter((i) => i.column === 2)?.map(iterate)}</div>
                </div>
            </div>,
            element
        )
    } else if (element && simpleMegaMenu) {
        menuContent = (
            <div className={cn(css.simpleMegaMenu, { [css.simpleMegaMenuOpen]: open })} ref={menuRef}>
                <div className={css.menuGroupLinks}>
                    {mega.blockLinks?.map((i) => {
                        return (
                            <a key={i.id} href={i.link}>
                                {i.title}
                            </a>
                        )
                    })}
                </div>
            </div>
        )
    }
    const pathname = usePathname()
    const active = useMemo(() => mega.blockLinks?.some((i) => i.link === pathname), [pathname, mega.blockLinks])
    return (
        <li className={cn(css.headerMenuItem, css.headerMenuItemMega, { [css.headerMenuItemOpen]: open })} data-active={active} ref={liRef}>
            <span className={css.headerMenuItemLink}>
                {title} <ArrowDown className={css.headerMenuItemArrow} />
            </span>
            {menuContent}
        </li>
    )
}
